import { isEmpty } from 'lodash';

import * as types from './constants';

const initialState = {
  showPreview: false,
  data: {
    name: '',
    max_items_per_user: null,
    max_value_per_user: null,
    // 'split' (Split-created) or 'shopify' - drives which offer fields are read-only across the offer views
    offers_source: 'split',
    offers: []
  },
  errors: {},
  unsavedChanges: false
};

const actions = {
  [types.LOOKBOOK_MANAGE_FLOW_SHOW_PREVIEW]: (state, { payload }) => {
    return { ...state, showPreview: payload };
  },

  [types.LOOKBOOK_MANAGE_FLOW_SET_PROP]: (state, { payload }) => {
    // Per-offer API errors are matched to offers by array index (see LookbookOffers), so any edit to the
    // offers array (add/delete/reorder/field change) makes the previous error set stale or, worse, misattributed
    // to whatever offer now occupies that index. Drop them here rather than in every offer mutation site.
    const errors = payload.prop === 'offers' && state.errors?.offers
      ? { ...state.errors, offers: undefined }
      : state.errors;

    return {
      ...state,
      data: {
        ...state.data,
        [payload.prop]: payload.value
      },
      errors,
      unsavedChanges: !payload.disableUnsavedChanges
    };
  },

  [types.LOOKBOOK_MANAGE_FLOW_SET_DATA]: (state, { payload }) => {
    return {
      ...state,
      data: payload,
      unsavedChanges: false
    };
  },

  [types.LOOKBOOK_MANAGE_FLOW_SET_UNSAVED_CHANGES]: (state, { payload }) => {
    return { ...state, unsavedChanges: payload };
  },

  // `stock` is the read-only Shopify reference figure - it is never sent back on save, so applying it
  // must leave `unsavedChanges` exactly as it was rather than flagging a phantom edit. A sid missing
  // from the payload keeps its current `stock` (unknown stays unknown, it never becomes a phantom 0).
  [types.LOOKBOOK_MANAGE_FLOW_APPLY_STOCK]: (state, { payload }) => {
    const stockBySid = payload || {};
    if (Object.keys(stockBySid).length === 0) return state;

    const offers = (state.data.offers || []).map(offer => {
      // An offer's own `sid` names the Shopify *Product*, which has no stock of its own; only a
      // single-variant offer carries its variant's sid (as `variant_sid`) alongside it.
      if (isEmpty(offer.variants)) {
        const sid = offer.variant_sid || offer.sid;
        return sid && sid in stockBySid ? { ...offer, stock: stockBySid[sid] } : offer;
      }

      return {
        ...offer,
        variants: offer.variants.map(variant => (
          variant.sid && variant.sid in stockBySid ? { ...variant, stock: stockBySid[variant.sid] } : variant
        ))
      };
    });

    return { ...state, data: { ...state.data, offers } };
  },

  [types.LOOKBOOK_MANAGE_FLOW_SET_VARIANT_IMAGE]: (state, { payload }) => {
    const { offerId, variantIds, imageUrl, imageUploadId, offerImageUploadId } = payload;
    if (isEmpty(variantIds)) return state;

    const offers = (state.data.offers || []).map(offer => {
      if (offer.id !== offerId) return offer;

      return {
        ...offer,
        ...(offerImageUploadId !== undefined && { image_upload_id: offerImageUploadId }),
        variants: (offer.variants || []).map(variant => (
          variantIds.includes(variant.id) ? { ...variant, image_url: imageUrl, image_upload_id: imageUploadId } : variant
        ))
      };
    });

    return { ...state, data: { ...state.data, offers }, unsavedChanges: true };
  },

  [types.LOOKBOOK_MANAGE_FLOW_CLEAR]: () => {
    return { ...initialState };
  },

  [types.LOOKBOOK_MANAGE_FLOW_SET_ERRORS]: (state, { payload }) => {

    return {
      ...state,
      errors: {
        ...initialState.errors,
        ...payload
      }
    };
  },

  [types.LOOKBOOK_MANAGE_FLOW_CLEAR_ERROR]: (state, { payload }) => {
    const newErrors = {
      ...state.errors,
      [payload]: initialState.errors[payload]
    };

    return {
      ...state,
      errors: {
        ...newErrors,
        active: false
      }
    };
  },

  [types.LOOKBOOK_MANAGE_FLOW_CLEAR_ERRORS]: state => {
    return {
      ...state,
      errors: {
        ...state.errors,
        active: false
      }
    };
  }
};

const lookbookManageFlow = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default lookbookManageFlow;
